/* © CNES */
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import { useContext, useEffect, useState } from 'react';
import { FilterDataContext } from '../../../../../../context/FilterDataContext';
import {
  GRID_CODE,
  ORBIT_ABSOLUTE,
  ORBIT_RELATIVE,
} from '../../../../../shared/Constants';
import { isNumber, isTile } from '../../../../../shared/Utils';
import { ButtonUpload } from '../buttonUpload/ButtonUpload';
import './InnerWhere.scss';

/**
 * InnerWhere displays Where filter options as a TabPanel.
 * @returns InnerWhere Element.
 */
export const InnerWhere = ({ handleChange }: { handleChange: Function }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [absoluteOrbitValue, setAbsoluteOrbitValue] = useState<string>('');
  const [relativeOrbitValue, setRelativeOrbitValue] = useState<string>('');
  const [tileValue, setTileValue] = useState<string>('');
  const { filterData } = useContext(FilterDataContext);

  /**
   * onChange handle Tab value.
   * @param value the value absolute orbit.
   * @param key the orbit key from which the orbit is selected from.
   */
  const changeTabValue = (
    e: React.KeyboardEvent<HTMLInputElement>,
    key: string
  ) => {
    handleChange((e.target as HTMLTextAreaElement).value, key);
  };

  const [isTileInErrorAfterKeyDown, setIsTileInErrorAfterKeyDown] =
    useState<boolean>(false);

  useEffect(() => {
    const absoluteOrbitFilter = filterData[ORBIT_ABSOLUTE];
    const relativeOrbitFilter = filterData[ORBIT_RELATIVE];
    const tileFilter = filterData[GRID_CODE];

    const absoluteOrbit =
      absoluteOrbitFilter && Array.isArray(absoluteOrbitFilter)
        ? absoluteOrbitFilter.join(',')
        : '';
    setAbsoluteOrbitValue(absoluteOrbit);

    const relativeOrbit =
      relativeOrbitFilter && Array.isArray(relativeOrbitFilter)
        ? relativeOrbitFilter.join(',')
        : '';
    setRelativeOrbitValue(relativeOrbit);
    setTileValue(
      tileFilter && typeof tileFilter === 'string' ? tileFilter : ''
    );
  }, [filterData]);

  /**
   * Validate tile input based on regex pattern.
   * @param event the input event.
   * @param validatePattern boolean to check if the input matches the regex pattern.
   */
  const validateTileInput = (
    event: React.FormEvent<HTMLInputElement>,
    validatePattern: boolean
  ) => {
    const inputValue = (event.target as HTMLInputElement).value;

    // validatePattern is the result of the regex against the whole input string
    if (validatePattern) {
      const upperValue = inputValue.toUpperCase();
      setTileValue(upperValue);
      setIsTileInErrorAfterKeyDown(false);
    }
  };

  /**
   * Check if the input is invalid tile identifier
   * @param tileValue the tile identifier input value
   * @returns boolean indicating if the tile input is invalid
   */
  const isTileInputInValid = (tileValue: string): boolean => {
    return (
      (tileValue.charAt(0) !== 'T' &&
        tileValue.length === 5 &&
        !isTile(tileValue)) ||
      (tileValue.length === 6 && !isTile(tileValue))
    );
  };

  return (
    <div className="where-container">
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        <TabPanel header="Tile">
          <InputText
            placeholder="MGRS Tile identifier, ex : T31TCJ or 31TCJ"
            className="inputTile"
            value={tileValue}
            keyfilter={/^[A-Za-z0-9]{0,6}$/}
            validateOnly={true}
            onInput={validateTileInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (isTile(tileValue)) {
                  changeTabValue(e, GRID_CODE);
                  setIsTileInErrorAfterKeyDown(false);
                } else {
                  setIsTileInErrorAfterKeyDown(true);
                }
              }
            }}
          />
          {/* Display error message if tileValue is not valid */}
          {(isTileInputInValid(tileValue) || isTileInErrorAfterKeyDown) && (
            <div className="error-message">
              Please enter a valid MGRS tile identifier (e.g., T31TCJ or 31TCJ).
            </div>
          )}
        </TabPanel>
        <TabPanel header="Orbit">
          <InputText
            placeholder="Absolute orbit number"
            className="inputOrbit"
            value={absoluteOrbitValue}
            onChange={(e) => {
              if (isNumber(e)) {
                setAbsoluteOrbitValue(e.target.value);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                changeTabValue(e, ORBIT_ABSOLUTE);
              }
            }}
          />
          <InputText
            placeholder="Relative orbit number"
            className="inputOrbit"
            value={relativeOrbitValue}
            onChange={(e) => {
              if (isNumber(e)) {
                setRelativeOrbitValue(e.target.value);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                changeTabValue(e, ORBIT_RELATIVE);
              }
            }}
          />
        </TabPanel>
        <TabPanel header="JSON">
          <p className="styleCnes">
            Select a GeoJSON (.geojson) file by clicking the{' '}
            <strong>Upload</strong> button below.
          </p>
          <ButtonUpload uploadFileExtention={'GeoJSON'} />
        </TabPanel>
        <TabPanel header="SHP">
          <p className="styleCnes">
            <strong>The shapefile </strong> (SHP) must be provided in the form
            of an archive(.zip) which contains at least the following 4 file:
          </p>
          <ul className="styleCnes">
            <li>
              SHP(.shp) contains spatial information (geographic entities)
            </li>
            <li>DBF (.dbf): contains attribute information</li>
            <li>SHX (.shx): contains the geometry index</li>
            <li>
              PRJ (.prj): contains information about the coordinate system.
            </li>
          </ul>
          <p className="styleCnes">
            All files must have the same name as the shapefile archive (.zip).
          </p>
          <ButtonUpload uploadFileExtention={'SHP'} />
        </TabPanel>
        <TabPanel header="KML">
          <div className="styleCnes">
            <p style={{ marginBottom: '0' }}>
              <strong>Keyhole Markup Language</strong>
            </p>
            Select a KML (.kml) file by clicking the UPLOAD button below.
          </div>
          <ButtonUpload uploadFileExtention={'KML'} />
        </TabPanel>
        <Button className="buttonUpload" label="Upload" icon="pi pi-upload" />
      </TabView>
    </div>
  );
};
